/* eslint-disable no-nested-ternary */

import { FC, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaRegCopy, FaLongArrowAltDown } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { Col, Row, Tag, Typography, Popconfirm, Tooltip, Popover } from 'antd';
import { CloseCircleOutlined } from '@ant-design/icons';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import UserAvatarPopover from 'components/UserAvatarPopover';
import RichTextEditor from 'components/RichTextEditor';
import LinkExternalIcon from 'components/LinkExternalIcon';
import RequestTimerDropdown from 'components/Timer/RequestTimerDropdown';
import { formatEstimateRange, formatTimeHM, getStatusColor, getDueDateInfo, formatCommentCreatedAt } from 'utils/string.utils';
import appRoutes from 'config/appRoutes';
import { UserResponse } from 'types';
import { getRequestStatusLabel } from '../../config/getRequestStatusOptions';
import { parseFormBuilder } from '../RequestUpdate';
import RequestIcon from '../RequestIcon';
import EscalationBadge from '../EscalationBadge';
import FrozenPopover from '../FrozenPopover';

import style from './index.module.less';

const { Title } = Typography;

export const mapTempColors = {
  hot: 'red',
  cold: 'blue',
  warm: 'orange',
};

export const mapTempValueColors = {
  1: 'blue',
  2: 'orange',
  3: 'red',
};

export const mapTempBorderColors = {
  hot: '#EAA9A0',
  cold: '#A6D4FB',
  warm: '#F5D699',
};

export const mapTempTooltip = {
  hot: 'Interaction in the last 7 days',
  warm: 'Interaction in the last 30 days',
  cold: 'No interaction in the last 30 days',
  frozen: 'Has 3 admin comments without response in last 30 days',
  overdue: 'Requests that should already have been completed',
};

interface RequestListItemProps {
  item: any;
  to: string;
  showClient?: boolean;
  showHours?: boolean;
  hideColumns?: string[];
  onRemove?: (item: any) => Promise<void>;
  onAddedTimelog?: () => void;
}

const formatMinutesToHM = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${mins.toString().padStart(2, '0')} h`;
};

export function hasOnlyImage(html: string): boolean {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const p = doc.body.querySelector('p');

  if (!p) return false;

  return p.children.length === 1 && p.children[0].tagName.toLowerCase() === 'img' && p.textContent?.trim() === '';
}

const RequestListItem: FC<RequestListItemProps> = ({
  item,
  to,
  showClient = false,
  showHours = false,
  hideColumns = [],
  onRemove,
  onAddedTimelog,
}) => {
  const user: UserResponse = useSelector((store: any) => store.auth.user);
  const [removing, setRemoving] = useState(false);

  const { md, lg } = useBreakpoint();

  const formBuilder = useMemo(() => parseFormBuilder(item), [item]);

  const isCompleted = item.status === 'completed';

  const clientName = item.project?.customer?.business_name ?? 'No client';

  const responsible = item.related_users.find((option: any) => option.pivot?.type === 'responsible');

  const isFreelancer = user.contact.type === 'freelancer';

  const hoursSpent = formatTimeHM(item.time_spent ?? 0);
  const hoursEstimate = formatEstimateRange(item.estimated_min, item.estimated_max);
  const isOverEstimate = item.estimated_max > 0 && item.time_spent > item.estimated_max;

  const temp = item.temperature as keyof typeof mapTempColors | undefined;

  const hasLatestCommentOnlyAnImage = item.latest_comment ? hasOnlyImage(item.latest_comment.text) : false;

  let color: string | undefined;

  if (temp && !item.ghosted) {
    color = mapTempBorderColors[temp];
  } else if (!temp && !item.ghosted && ['completed', 'cancelled'].includes(item.status)) {
    color = 'transparent';
  } else {
    color = '#aaaaaa';
  }

  const { dueDate, isOverdue, isWarning, isToday } = getDueDateInfo(item);

  return (
    <Link to={to} className={style.link}>
      <Row className={style.rowItem} style={{ columnGap: 8 }} gutter={[0, 6]}>
        <Col
          flex="auto"
          className={style.colRequestRow}
          style={{
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            columnGap: 12,
            borderLeft: `4px solid ${color}`,
            borderRadius: '4px',
          }}
        >
          <div className={style.formIcon}>
            <RequestIcon formId={formBuilder.id} />
          </div>

          <div style={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>
            <FrozenPopover item={item}>
              <Title level={5} className={style.title} ellipsis>
                {md ? item.subject : <Tooltip title={item.subject}>{item.subject}</Tooltip>}
              </Title>
            </FrozenPopover>
          </div>
          <div className={style.colEscalationInline}>
            <EscalationBadge item={item} />
          </div>
        </Col>

        <Col flex="none" className={style.colRequestRow}>
          <Row className={style.rowExtraInfo} align="middle" style={{ columnGap: 8 }} gutter={[0, 6]}>
            {item.latest_comment && (
              <Col className={style.colLatestComment}>
                <Link to={`${to}?target_comment_id=${item.latest_comment.uuid}`} className={style.linkLatestComment}>
                  <div style={{ flex: 'none' }}>
                    <UserAvatarPopover user={item.latest_comment.creator} />
                  </div>

                  <Tooltip title={formatCommentCreatedAt(item.latest_comment.created_at)}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div onMouseEnter={(e) => e.preventDefault?.()} style={{ cursor: 'inherit' }}>
                        <span>
                          {hasLatestCommentOnlyAnImage ? (
                            <div className="h-full">[Image attached]</div>
                          ) : (
                            <RichTextEditor
                              key={item.latest_comment.text}
                              className="comment-styles resume"
                              initialValue={item.latest_comment.text}
                              readonly
                              noContainer
                            />
                          )}
                        </span>
                      </div>
                    </div>
                  </Tooltip>
                </Link>
              </Col>
            )}

            {!hideColumns.includes('project') && showClient && (
              <Col className={style.colProject}>
                <LinkExternalIcon to={`?client_uuid=${item.project?.customer?.uuid}`} responsiveListing>
                  {lg ? clientName : <Tooltip title={clientName}>{clientName}</Tooltip>}
                </LinkExternalIcon>
              </Col>
            )}

            {!hideColumns.includes('escalation') && (
              <Col className={style.colEscalation}>
                <EscalationBadge item={item} />
              </Col>
            )}

            {!hideColumns.includes('delivery') && showClient && (
              <Col className={style.colDelivery}>
                {item.target_date && (
                  <Tooltip title={isCompleted ? 'Completed' : 'Delivery'}>
                    <span
                      style={{
                        color: isOverdue ? 'red' : isWarning || isToday ? 'orange' : undefined,
                        textDecoration: isToday ? 'underline' : undefined,
                        textDecorationThickness: isToday ? '2px' : undefined,
                        textUnderlineOffset: isToday ? '5px' : undefined,
                      }}
                    >
                      {dueDate}
                    </span>
                  </Tooltip>
                )}
              </Col>
            )}

            {!hideColumns.includes('hours') && showHours && (
              <Col className={style.colHours}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {onAddedTimelog && !isFreelancer && item.project && (
                    <span
                      role="presentation"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                      <RequestTimerDropdown request={item} icon="plus" onAdded={onAddedTimelog} />
                    </span>
                  )}
                  {item.burndown?.status === 'under' && (
                    <Popover
                      content={`Expected: ${formatMinutesToHM(item.burndown.expected_minutes)}`}
                      placement="top"
                    >
                      <FaLongArrowAltDown style={{ color: 'red', cursor: 'pointer', fontSize: '12px' }} />
                    </Popover>
                  )}
                  <div style={{ marginLeft: 'auto' }}>
                    <Tooltip title="Spent">
                      <span style={{ color: isOverEstimate ? 'red' : undefined }}>{hoursSpent}</span>
                    </Tooltip>
                    {' | '}
                    <Tooltip title="Estimate">{hoursEstimate}</Tooltip>
                  </div>
                </div>
              </Col>
            )}

            <Col className={responsible ? style.colResponsible : style.colResponsibleEmpty}>
              {responsible && <UserAvatarPopover user={responsible} size="lg40" />}
            </Col>

            <Col className={style.colStatus}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'right' }}>
                <Tooltip title="Status">
                  <Tag style={{ marginRight: 0 }} color={getStatusColor(item.status)}>
                    {getRequestStatusLabel(item.status)}
                  </Tag>
                </Tooltip>

                {!onRemove && !isFreelancer ? (
                  <Tooltip title="Duplicate" className="flex items-center justify-center">
                    <Link to={`${appRoutes.REQUESTS}/${item.uuid}/duplicate`} className={style.colStatusDuplicate}>
                      <FaRegCopy size={16} />
                    </Link>
                  </Tooltip>
                ) : undefined}
              </div>

              <Row justify="end" align="middle" wrap={false} className={style.colStatusDetails} style={{ columnGap: 12 }}>
                {onRemove && !isFreelancer ? (
                  <Col style={{ marginTop: 2 }}>
                    <Tooltip title="Duplicate">
                      <Link to={`${appRoutes.REQUESTS}/${item.uuid}/duplicate`}>
                        <FaRegCopy />
                      </Link>
                    </Tooltip>
                  </Col>
                ) : undefined}

                {onRemove ? (
                  <Col style={{ textAlign: 'right', marginTop: 2 }}>
                    <Popconfirm
                      disabled={removing}
                      title="Do you want to remove this request from the task?"
                      onConfirm={(event) => {
                        event?.stopPropagation();
                        setRemoving(true);
                        onRemove(item).finally(() => setRemoving(false));
                      }}
                      onCancel={(event) => event?.stopPropagation()}
                    >
                      <Tooltip title="Remove">
                        <CloseCircleOutlined className="red pointer" />
                      </Tooltip>
                    </Popconfirm>
                  </Col>
                ) : undefined}
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Link>
  );
};

export default RequestListItem;
